'use client'

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Dialog, DialogContent, Skeleton, toast } from '@ui/web/components'

import type { CreateNewChildValues } from './create-new-step'
import { useSharedTRPC } from '../../context'
import { ConfirmStep } from './confirm-step'
import { ConnectExistingStep } from './connect-existing-step'
import { CreateNewStep } from './create-new-step'
import { RejectStep } from './reject-step'

export { HomeConnectRequestSummary } from './request-summary'
export type { HomeConnectRequestDetail } from './request-summary'

type Step = 'confirm' | 'existing' | 'new' | 'reject'

interface HomeConnectResponseDialogProps {
  requestId: string
  onClose: () => void
}

/**
 * The parent's answer to a Sambung Sekolah-Rumah request, as a four-step dialog.
 *
 * One component rather than four call sites because the steps share the request and walk back and
 * forth between each other — every screen in the designs has a "kembali ke permohonan".
 */
export function HomeConnectResponseDialog({ requestId, onClose }: HomeConnectResponseDialogProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('confirm')

  const { data: detail, isPending, isError } = useQuery(trpc.homeConnect.getRequest.queryOptions({ requestId }))

  const accept = useMutation(trpc.homeConnect.accept.mutationOptions())
  const reject = useMutation(trpc.homeConnect.reject.mutationOptions())
  const isSubmitting = accept.isPending || reject.isPending

  /**
   * Everything about this child is now different — which organizations they belong to, whose roster
   * they appear on, and (after a merge) every lookup table. Invalidating the whole cache is blunt
   * but correct; enumerating the affected keys would be a list that silently rots.
   */
  const settle = async () => {
    await queryClient.invalidateQueries()
    onClose()
  }

  const handleAccept = async (mode: Parameters<typeof accept.mutateAsync>[0]['mode']) => {
    try {
      await accept.mutateAsync({ requestId, mode })

      // A merge deletes the child's cached totals and rebuilds them in the background, so the XP and
      // reading charts read empty for a few minutes. Saying so is the difference between "still
      // working" and "it lost my child's progress".
      if (mode.kind === 'merge') {
        toast.success('Anak berhasil tersambung. Data belajarnya sedang disiapkan, mungkin butuh beberapa menit.')
      } else {
        toast.success('Anak berhasil tersambung dengan sekolah.')
      }

      await settle()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal menerima permohonan')
    }
  }

  const handleReject = async (reason: string) => {
    try {
      await reject.mutateAsync({ requestId, reason })
      toast.success('Permohonan ditolak.')
      await settle()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal menolak permohonan')
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose()
      }}
    >
      <DialogContent className='srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-lg srd:flex-col srd:gap-y-4 srd:overflow-y-auto'>
        {isPending && <Skeleton className='srd:h-[420px] srd:w-full' />}

        {isError && (
          <p className='srd:text-error srd:py-8 srd:text-center'>Permohonan tidak ditemukan atau sudah kedaluwarsa.</p>
        )}

        {detail && step === 'confirm' && (
          <ConfirmStep
            detail={detail}
            onContinue={(choice) => setStep(choice === 'existing' ? 'existing' : 'new')}
            onReject={() => setStep('reject')}
            onClose={onClose}
          />
        )}

        {detail && step === 'existing' && (
          <ConnectExistingStep
            requestId={requestId}
            detail={detail}
            isSubmitting={isSubmitting}
            onConfirm={(childId) => void handleAccept({ kind: 'merge', childId })}
            onCreateNew={() => setStep('new')}
            onBack={() => setStep('confirm')}
          />
        )}

        {detail && step === 'new' && (
          <CreateNewStep
            detail={detail}
            isSubmitting={isSubmitting}
            onConfirm={(values: CreateNewChildValues) =>
              void handleAccept({ kind: 'adopt', name: values.name, image: values.image })
            }
            onConnectExisting={() => setStep('existing')}
            onBack={() => setStep('confirm')}
          />
        )}

        {detail && step === 'reject' && (
          <RejectStep
            detail={detail}
            isSubmitting={isSubmitting}
            onReject={(reason) => void handleReject(reason)}
            onBack={() => setStep('confirm')}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
